import React, { memo, useEffect } from 'react'; // Добавить memo
import { IconButton, Link, Stack, TableRow, Typography } from '@mui/material';
import MyTableCell from '../../../components/material-ui/adaptive/MyTableCell';
import CusCheckbox from '../../../components/material-ui/CusCheckbox';
import CusItemTooltip from '../../../components/material-ui/CusItemTooltip';
import ShowContainer from '../../../components/material-ui/ShowContainer';
import { colorByUsersStatus, popupInfoByType } from '../../../enums/enums';
import { arraysEqual, dispatchCusEvent, getOnlyData } from '../../../utils';
import dayjs from 'dayjs';
import { Lock, LockOpen, Visibility } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useColor from '../../../hooks/site/useColor';
import { IconCircle, IconMail, IconMailOpened } from '@tabler/icons-react';
import useBootstrapSize from '../../../hooks/useBootstrapSize';
import UserInfoMobile from '../modal/UserInfoMobile';

const UserTableRow = memo(
  ({
    user = [],
    index,
    beginValueIndex,
    isSelected,
    isChecked,
    setSelectUserIndx,
    popupTextByStatus,
    iconByStatus,
    setChosenList,
    setOpenShowUserMobile
  }) => {
    const getDate = (date) => {
      if (!date) return '';
      return getOnlyData(dayjs(date), '', 'DD.MM.YYYY HH:mm');
    };

    const { t } = useTranslation();
    const { success, error, grey, primary } = useColor();
    const { isMobile } = useBootstrapSize();

    const userId = user[0];
    const userLogin = user[1];
    const userType = user[2];
    const userStatus = user[3];
    const userOnline = user[4];
    const userAutoBlockDep = user[5];
    const userBlockDep = user[6];
    const unreadMes = user[7];
    const tgId = user[8];
    const userEmail = user[9];

    const userTypeInfo = popupInfoByType[userType];
    const Icon = userTypeInfo?.icon;

    const textDepositLock = userBlockDep
      ? `${t('Deposits are blocked')}. ${t('Deposit creation blocked until')} ${getDate(userBlockDep)}`
      : userAutoBlockDep
        ? 'Automatic blocking is enabled'
        : 'Automatic blocking is prohibited';

    const IconDeposit = userBlockDep ? (
      <Lock color="error" />
    ) : userAutoBlockDep ? (
      <LockOpen color={'disabled'} />
    ) : (
      <LockOpen color={'success'} />
    );

    const onChangeRow = () => {
      setChosenList((prev) =>
        prev.includes(index) ? prev.filter((id) => id !== index) : [...prev, index]
      );
    };

    const onEyeClick = () => {
      setOpenShowUserMobile((prev) => (prev == userId ? false : userId));
    };

    return (
      <>
        <TableRow
          key={userId + '_' + index}
          hover
          role="checkbox"
          tabIndex={-1}
          selected={isSelected}
          onClick={() => {
            setSelectUserIndx(index);
            onChangeRow();
          }}>
          <MyTableCell isLess width={70}>
            <Stack direction={'row'} gap={0} alignItems={'center'}>
              <CusCheckbox
                onClick={(e) => {
                  e.stopPropagation();
                  onChangeRow();
                }}
                checked={isChecked}
              />

              {beginValueIndex + index + 1}
            </Stack>
          </MyTableCell>
          <MyTableCell isLess>
            <CusItemTooltip
              text={userOnline ? 'Online' : 'Offline'}
              elem={
                <IconCircle
                  fill={userOnline ? success.main : error.main}
                  color={userOnline ? success.main : error.main}
                  size={20}
                />
              }
            />
          </MyTableCell>
          <MyTableCell isLess>
            <Stack direction={'row'} justifyContent={'center'}>
              <CusItemTooltip
                text={popupTextByStatus[userStatus]}
                elem={
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectUserIndx(index);
                      dispatchCusEvent('openChangeStatus');
                    }}>
                    {iconByStatus[userStatus]}
                  </IconButton>
                }
              />
            </Stack>
          </MyTableCell>

          <MyTableCell isLess>
            <Stack direction={'row'} justifyContent={'center'}>
              <CusItemTooltip
                text={textDepositLock}
                elem={
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectUserIndx(index);
                      dispatchCusEvent('openBlockDeposit');
                    }}>
                    {IconDeposit}
                  </IconButton>
                }
              />
            </Stack>
          </MyTableCell>

          <MyTableCell isLess>
            <ShowContainer condition={tgId}>
              <Link
                component={'button'}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectUserIndx(index);
                  dispatchCusEvent('openWriteTg');
                }}
                sx={{ color: error.main }}>
                {unreadMes ? (
                  <Stack direction={'row'} gap={0.5} alignItems={'center'} justifyContent={'start'}>
                    <CusItemTooltip
                      text={'Unread messages'}
                      elem={<IconMail size={18} color={error.main} />}
                    />
                    <Typography>{unreadMes}</Typography>
                  </Stack>
                ) : (
                  <Stack direction={'row'} justifyContent={'start'}>
                    <CusItemTooltip
                      text={'Write a message'}
                      elem={<IconMailOpened size={18} color={primary.main} />}
                    />
                  </Stack>
                )}
              </Link>
            </ShowContainer>
          </MyTableCell>
          <MyTableCell isLess>
            <ShowContainer condition={userTypeInfo} plug={userType}>
              <CusItemTooltip
                text={userTypeInfo?.text || ''}
                elem={<Icon size={18} color={colorByUsersStatus[userType] || grey[500]} />}
              />
            </ShowContainer>
          </MyTableCell>
          <MyTableCell isLess type="numeric">
            <Stack direction={'row'} gap={1} justifyContent={'center'}>
              {userId}
            </Stack>
          </MyTableCell>
          <MyTableCell isLess>
            <Stack>
              <Typography>{userLogin}</Typography>
              {userEmail && userLogin !== userEmail && (
                <Typography color="textSecondary" fontSize={13}>
                  {userEmail}
                </Typography>
              )}
            </Stack>
          </MyTableCell>
          <ShowContainer condition={isMobile}>
            <MyTableCell isLess>
              <IconButton onClick={onEyeClick}>
                <Visibility />
              </IconButton>
            </MyTableCell>
          </ShowContainer>
        </TableRow>
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      arraysEqual(prevProps.user, nextProps.user, true) &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.isChecked === nextProps.isChecked
    );
  }
);

export default UserTableRow;
